import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange
}) {
  if (totalItems === 0) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers array
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 10;

    let start = Math.max(1, currentPage - 4);
    let end = Math.min(
      totalPages,
      start + maxVisible - 1
    );

    if (end - start < maxVisible - 1) {
      start = Math.max(
        1,
        end - maxVisible + 1
      );
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div
      className="glass-panel"
      style={{
        padding: '14px 22px',
        marginTop: '24px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        background: '#ffffff',
        borderRadius: '18px',
        boxShadow: '0 8px 20px -5px rgba(99, 102, 241, 0.08)'
      }}
    >
      {/* Left: Summary & Page Size Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.86rem', color: 'var(--text-muted)' }}>
        <div>
          Showing <strong style={{ color: '#0f172a' }}>{startItem}</strong> to <strong style={{ color: '#0f172a' }}>{endItem}</strong> of <strong style={{ color: '#4f46e5' }}>{totalItems}</strong> domains
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              onItemsPerPageChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="form-control"
            style={{ padding: '4px 8px', width: 'auto', fontSize: '0.82rem', borderRadius: '8px', background: '#f8fafc' }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Right: Pagination Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {/* First Page */}
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{ opacity: currentPage === 1 ? 0.4 : 1, padding: '6px 8px' }}
          title="First Page"
        >
          <ChevronsLeft size={16} />
        </button>

        {/* Previous Page */}
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{ opacity: currentPage === 1 ? 0.4 : 1, padding: '6px 8px' }}
          title="Previous Page"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Page Numbers */}
        {getPageNumbers().map(pageNum => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            style={{
              padding: '6px 12px',
              borderRadius: '9px',
              fontWeight: '700',
              fontSize: '0.85rem',
              cursor: 'pointer',
              border: pageNum === currentPage ? 'none' : '1px solid #cbd5e1',
              background: pageNum === currentPage ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '#ffffff',
              color: pageNum === currentPage ? '#ffffff' : '#334155',
              boxShadow: pageNum === currentPage ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
              transition: 'all 0.15s'
            }}
          >
            {pageNum}
          </button>
        ))}

        {/* Next Page */}
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{ opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1, padding: '6px 8px' }}
          title="Next Page"
        >
          <ChevronRight size={16} />
        </button>

        {/* Last Page */}
        <button
          className="btn btn-secondary btn-icon"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{ opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1, padding: '6px 8px' }}
          title="Last Page"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
}
